import { cn } from "@/lib/utils";
import { WebPreviewNavigation } from "../ai-elements/web-preview";
import { Button } from "../ui/button";
import {
  HatGlassesIcon,
  MousePointerClickIcon,
  UserIcon,
  SmartphoneIcon,
  ExternalLinkIcon,
  SettingsIcon,
} from "lucide-react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
} from "@heroicons/react/16/solid";
import { usePreviewHistory } from "./use-preview-history";
import { Input } from "../ui/input";
import { useEffect, useMemo, useState } from "react";
import { imagineStore } from "@/stores/imagine.store";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { createIdGenerator } from "ai";
import { useAuth } from "@/hooks/user-auth";
import { useImagineChat } from "@/providers/imagine-chat-provider";
import { ButtonGroup } from "../ui/button-group";
import { ReleaseButton } from "./release";
import { AdvancedActions } from "./advanced-actions";
import { PromptAddonType } from "@/stores/slices/prompt-context.slice";
import { imagineConfigStore } from "@/web-components";
import { trackAnalyticsEvent } from "@/lib/analytics";

const navigationIconClassName =
  "transition-all size-4 text-muted-foreground bg-transparent hover:bg-transparent group-hover:text-primary";
const navigationButtonClassName = "size-6 rounded-sm mx-1 group";

type CanvasPanelTopBarProps = {
  onRequestModeChange?: (mode: "preview" | "code") => void;
};

export function CanvasPanelTopBar({
  onRequestModeChange,
}: CanvasPanelTopBarProps) {
  const isReadonly = imagineStore.use.isReadonly();
  const addAddon = imagineStore.use.promptContext.addAddon();
  const viewMode = imagineStore.use.previewState.viewMode();
  const setViewMode = imagineStore.use.previewState.setViewMode();
  const isElementSelectorEnabled =
    imagineStore.use.previewState.isElementSelectorEnabled();
  const setIsElementSelectorEnabled =
    imagineStore.use.previewState.setElementSelectorEnabled();
  const sandboxInstrumented = imagineStore.use.sandboxState.instrumented();
  const initialUrl = imagineStore.use.previewState.previewUrlWithPreviewToken();
  const refreshIframe = imagineStore.use.previewState.refreshIframe();
  const sandboxClient = imagineStore.use.sandboxState.sandboxClient();
  const { goForward, canGoForward, canGoBack, goBack, current } =
    usePreviewHistory(initialUrl! || new URL("https://imagine.dev"));
  const uiMode = imagineStore.use.ui.mode();
  const projectId = imagineStore.use.projectId();
  const chatPanelCollapsed = imagineStore.use.ui.chatPanelCollapsed();
  const setChatPanelCollapsed = imagineStore.use.ui.setChatPanelCollapsed();

  const addressBarValue = useMemo(() => {
    const url = new URL(current);
    const urlWithoutPreviewToken = new URL(url.toString());
    urlWithoutPreviewToken.searchParams.delete("imaginePreviewToken");
    const pathWithSearchAndHash = `${urlWithoutPreviewToken.pathname}${urlWithoutPreviewToken.search}${urlWithoutPreviewToken.hash}`;
    return pathWithSearchAndHash;
  }, [current]);

  // Emit explicit top-bar events (instead of autocapture) so we keep structured,
  // analytics rather than noisy DOM click payloads.

  useEffect(() => {
    if (!isReadonly) {
      addAddon(PromptAddonType.PREVIEW_ROUTE, {
        path: addressBarValue,
        removable: false,
        singleton: true,
        hidden: true,
      });
    }
  }, [addressBarValue, addAddon, isReadonly]);

  useEffect(() => {
    if (!isReadonly && sandboxClient && sandboxInstrumented) {
      sandboxClient.toggleSelector(isElementSelectorEnabled);
    }
  }, [
    isElementSelectorEnabled,
    sandboxInstrumented,
    sandboxClient,
    isReadonly,
  ]);

  return (
    <WebPreviewNavigation className="h-12">
      <div className="flex gap-2 px-1 w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="shadow-none size-8 cursor-pointer text-muted-foreground"
            onClick={() => {
              const next = !chatPanelCollapsed;
              setChatPanelCollapsed(next);
              void trackAnalyticsEvent("canvas_top_bar.chat_panel_toggled", {
                collapsed: next,
                projectId: projectId ?? undefined,
              });
            }}
          >
            {chatPanelCollapsed ? (
              <ChevronRightIcon className="size-4" />
            ) : (
              <ChevronLeftIcon className="size-4" />
            )}
          </Button>
          <ModeToggle
            onRequestModeChange={onRequestModeChange}
            disabled={isReadonly || !sandboxInstrumented}
          />
        </div>

        {uiMode === "preview" && (
          <>
            <div
              className={cn(
                "h-6.5 w-80 bg-sidebar border rounded-sm px-2 flex items-center text-muted-foreground",
              )}
            >
              <Button
                size="icon"
                variant="ghost"
                className={cn(navigationButtonClassName, "size-3")}
                onClick={() => {
                  goBack();
                  void trackAnalyticsEvent(
                    "canvas_top_bar.navigation_clicked",
                    { direction: "back", projectId: projectId ?? undefined },
                  );
                }}
                disabled={isReadonly || !sandboxInstrumented || !canGoBack}
              >
                <ChevronLeftIcon
                  className={cn(navigationIconClassName, "size-4.5")}
                />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className={cn(navigationButtonClassName, "size-3")}
                onClick={() => {
                  goForward();
                  void trackAnalyticsEvent(
                    "canvas_top_bar.navigation_clicked",
                    { direction: "forward", projectId: projectId ?? undefined },
                  );
                }}
                disabled={isReadonly || !sandboxInstrumented || !canGoForward}
              >
                <ChevronRightIcon
                  className={cn(navigationIconClassName, "size-4.5")}
                />
              </Button>
              <Input
                className="flex-1 px-2 flex h-5 border-none"
                style={{ fontSize: "0.7rem", color: "var(--primary)" }}
                value={
                  isReadonly || !sandboxInstrumented ? addressBarValue : ""
                }
                disabled={true}
              />
              <Button
                size="icon"
                variant="ghost"
                className={cn(
                  navigationButtonClassName,
                  "size-3 hover:rotate-180 transition-transform duration-300",
                )}
                onClick={() => {
                  refreshIframe();
                  void trackAnalyticsEvent(
                    "canvas_top_bar.navigation_clicked",
                    { direction: "refresh", projectId: projectId ?? undefined },
                  );
                }}
                disabled={isReadonly || !sandboxInstrumented}
              >
                <ArrowPathIcon
                  className={cn(navigationIconClassName, "size-4")}
                />
              </Button>
            </div>
            <div className="flex items-center">
              <Button
                size="icon"
                variant="ghost"
                className={cn(
                  navigationButtonClassName,
                  isElementSelectorEnabled &&
                    "bg-primary/10 hover:bg-primary/20",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  const next = !isElementSelectorEnabled;
                  setIsElementSelectorEnabled(next);
                  void trackAnalyticsEvent(
                    "canvas_top_bar.element_selector_toggled",
                    { enabled: next, projectId: projectId ?? undefined },
                  );
                }}
                disabled={isReadonly || !sandboxInstrumented}
              >
                <MousePointerClickIcon
                  className={cn(
                    navigationIconClassName,
                    isElementSelectorEnabled && "text-primary",
                  )}
                />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className={cn(
                  navigationButtonClassName,
                  viewMode === "mobile" && "bg-primary/10 hover:bg-primary/20",
                )}
                onClick={() => {
                  const next = viewMode === "desktop" ? "mobile" : "desktop";
                  setViewMode(next);
                  void trackAnalyticsEvent("canvas_top_bar.view_mode_toggled", {
                    viewMode: next,
                    projectId: projectId ?? undefined,
                  });
                }}
                disabled={isReadonly ? false : !sandboxInstrumented}
              >
                <SmartphoneIcon
                  className={cn(
                    navigationIconClassName,
                    viewMode === "mobile" && "text-primary",
                  )}
                />
              </Button>
              <OpenInNewTabButton
                disabled={isReadonly ? false : !sandboxInstrumented}
                currentUrl={current}
                isReadonly={isReadonly}
              />
              <AdvancedActions disabled={isReadonly}>
                <Button
                  size="icon"
                  variant="ghost"
                  className={cn(navigationButtonClassName, "mr-2")}
                >
                  <SettingsIcon className={cn(navigationIconClassName)} />
                </Button>
              </AdvancedActions>

              <ReleaseButton disabled={isReadonly || !sandboxInstrumented} />
            </div>
          </>
        )}
      </div>
    </WebPreviewNavigation>
  );
}

function OpenInNewTabButton({
  disabled,
  currentUrl,
  isReadonly,
}: {
  disabled: boolean;
  currentUrl: URL;
  isReadonly: boolean;
}) {
  const { user } = useAuth();
  const projectId = imagineStore.use.projectId();
  const constructAppwriteSitesPreviewUrl =
    imagineConfigStore.use.constructAppwriteSitesPreviewUrl();

  const openPreviewInNewTab = ({
    mode,
  }: {
    mode: "signed-out" | "current-user";
  }) => {
    const intermediateUrl = new URL(currentUrl.toString());

    if (mode === "signed-out") {
      // Set path to /sign-out
      const currentSubdomain = intermediateUrl.host.split(".")[0];
      const restHost = intermediateUrl.host.split(".").slice(1).join(".");
      // Get rid of "-preview"
      const subdomain = currentSubdomain!.replace("-preview", "");
      // Append random session ID, which results in an unauthenticated origin
      const sessionId = createIdGenerator({ size: 6 })();
      const randomizedSubdomain = `${subdomain}-session-${sessionId}`;
      intermediateUrl.host = `${randomizedSubdomain}.${restHost}`;
      // Remove imaginePreviewToken from URL, if exists
      intermediateUrl.searchParams.delete("imaginePreviewToken");
    }

    const targetUrl = intermediateUrl.toString();
    window.open(targetUrl, "_blank");
    void trackAnalyticsEvent("canvas_top_bar.preview_opened", {
      mode,
      url: targetUrl,
      projectId: projectId ?? undefined,
    });
  };

  const options = useMemo(() => {
    if (isReadonly) {
      return [
        {
          label: <div>Open in new tab</div>,
          icon: (
            <ExternalLinkIcon className="size-4 text-muted-foreground gap-6" />
          ),
          onClick: () => {
            const url = constructAppwriteSitesPreviewUrl(projectId!).toString();
            window.open(url, "_blank");
            void trackAnalyticsEvent("canvas_top_bar.preview_opened", {
              mode: "read-only",
              url,
              projectId: projectId ?? undefined,
            });
          },
          key: "open-in-new-tab",
        },
      ];
    } else {
      return [
        {
          label: <div>Preview as signed-out user</div>,
          icon: <HatGlassesIcon className="size-4 text-muted-foreground" />,
          onClick: () => openPreviewInNewTab({ mode: "signed-out" }),
          key: "signed-out",
        },
        {
          label: (
            <div>
              Preview as <span className="font-medium">{user?.email}</span>
            </div>
          ),
          icon: <UserIcon className="size-4 text-muted-foreground gap-6" />,
          onClick: () => openPreviewInNewTab({ mode: "current-user" }),
          key: "current-user",
        },
      ];
    }
  }, [openPreviewInNewTab, user, isReadonly]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          tabIndex={0}
          className={cn(navigationButtonClassName, "focus:outline-none")}
          disabled={disabled}
        >
          <ExternalLinkIcon className={cn(navigationIconClassName)} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={12}>
        {options.map((option) => (
          <DropdownMenuItem
            key={option.key}
            onClick={option.onClick}
            className="flex items-center justify-between gap-6 cursor-pointer"
          >
            {option.label}
            {option.icon}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ModeToggle({
  onRequestModeChange,
  disabled,
}: {
  onRequestModeChange?: (mode: "preview" | "code") => void;
  disabled: boolean;
}) {
  const { isGenerating } = useImagineChat();
  const mode = imagineStore.use.ui.mode();
  const setMode = imagineStore.use.ui.setMode();

  const options = [
    { value: "preview", label: "Preview" },
    { value: "code", label: "Code", disabled: isGenerating },
  ];

  return (
    <ButtonGroup
      options={options}
      value={mode}
      onChange={(value) => {
        const nextMode = value as "preview" | "code";
        if (onRequestModeChange) {
          onRequestModeChange(nextMode);
          return;
        }
        setMode(nextMode);
        void trackAnalyticsEvent("canvas.view_changed", {
          view: nextMode,
          projectId: imagineStore.getState().projectId ?? undefined,
        });
      }}
      disabled={disabled}
    />
  );
}
